import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * KidsTimeline — per-kid creator page. Mirrors /timeline/:uid but
 * filtered to kid-safe content and wrapped in the kids aesthetic.
 */
export default function KidsTimeline() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/kids-public/timeline/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="pt-32 text-white/60 text-center">Loading…</div>;
  if (!data) return (
    <div className="pt-32 px-6 text-center text-white">
      <div className="text-4xl">🤷</div>
      <div className="mt-2">Kid creator not found.</div>
      <Link to="/kids" className="inline-block mt-4 bg-yellow-300 text-black font-bold px-5 py-2 rounded-full">Back to Kids</Link>
    </div>
  );

  const { author, posts, stats } = data;

  return (
    <div data-testid="kids-timeline-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-4xl mx-auto text-white">
      <Link to="/kids" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-3"><ArrowLeft className="w-4 h-4" /> Back to Kids</Link>

      {/* Header card */}
      <div className="relative overflow-hidden rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-pink-400 via-fuchsia-500 to-amber-400 shadow-2xl ring-4 ring-white/25">
        <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none">
          <img src="/kids-learn-play-logo.png" alt="" className="w-48 h-auto" />
        </div>
        <div className="relative flex items-center gap-4 flex-wrap">
          <div className="w-28 h-28 rounded-full bg-white/25 ring-4 ring-white/40 flex items-center justify-center text-6xl shadow-xl">
            {author.picture ? <img src={author.picture} alt="" className="w-full h-full object-cover rounded-full" /> : <span>{author.avatar_emoji || "⭐"}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> {author.is_kid_bot ? "Kid Buddy" : "Kid Creator"}</div>
            <h1 data-testid="ktl-name" className="font-display font-black text-4xl md:text-5xl tracking-tighter drop-shadow mt-1">{author.name}</h1>
            <div className="text-white/90 text-sm">@{author.handle} · age {author.age || "?"}</div>
            {author.bio && <p data-testid="ktl-bio" className="mt-2 text-white/95 text-sm max-w-xl">{author.bio}</p>}
            <div className="mt-3 flex gap-4 text-sm">
              <div data-testid="ktl-posts-count"><b>{stats.post_count}</b> posts</div>
              <div data-testid="ktl-followers" className="inline-flex items-center gap-1"><Users className="w-4 h-4" /> <b>{stats.follower_count}</b> friends</div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts grid */}
      <h2 className="font-display font-black text-xl sm:text-2xl mt-6 mb-3">Their posts</h2>
      {posts.length === 0 && (
        <div data-testid="ktl-empty" className="bg-black/30 border border-dashed border-white/10 rounded-2xl p-6 text-center text-white/60">
          No posts yet.
        </div>
      )}
      <div data-testid="ktl-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.map((p) => (
          <div
            key={p.id}
            data-testid={`ktl-post-${p.id}`}
            className={`rounded-2xl p-4 bg-gradient-to-br ${p.color || "from-pink-400 to-fuchsia-500"} shadow-lg ring-2 ring-white/25 text-white`}
          >
            <div className="text-4xl leading-none drop-shadow">{p.emoji || "⭐"}</div>
            <div className="font-display font-black text-lg mt-1 leading-snug drop-shadow">{p.title}</div>
            <div className="text-white/70 text-[10px] mt-2">{new Date(p.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
